import _ from "lodash";
import { DateTime, Interval } from "luxon";
import { createSelector } from "reselect";
import { getWebsiteCurrencySelector } from "./selector";
import Fuse from "fuse.js";
import * as turf from "@turf/turf";
import { getWebsiteMetadata } from "../selectors/metadata";

const getHotelSearch = (state) => state.HotelSearch;

export const hotelsIsSearchingSelector = createSelector(
  [getHotelSearch],
  (data) => data.isSearching
);

export const hotelsShouldShowSelector = createSelector(
  [getHotelSearch],
  (data) => {
    return (
      data.hotels.length > 0 && data.hotels.some((h) => h.rooms.length > 0)
    );
  }
);

export const hotelSearchStripDataSelector = createSelector(
  [getHotelSearch],
  (data) => {
    const { checkIn, checkOut, destination_meta, adults, children, rooms } =
      data.searchData;

    return { checkIn, checkOut, destination_meta, adults, children, rooms };
  }
);

const getHotelFilters = (state) => state.HotelSearch.hotel_filters;

const getHotels = (state, props = { min_rating: 0 }) => {
  const { min_rating } = props;
  if (!min_rating) {
    return state.HotelSearch.hotels;
  }

  return state.HotelSearch.hotels.filter(
    (h) => h.metadata.rating >= min_rating
  );
};
const getSearchData = (state) => state.HotelSearch.searchData;

export const getHotelsSelector = createSelector(
  [getHotels, getSearchData, getHotelFilters, getWebsiteMetadata],
  (hotels, searchData, filters, website_metadata) => {
    const checkIn = DateTime.fromISO(searchData.checkIn);
    const checkOut = DateTime.fromISO(searchData.checkOut);
    const stay = Interval.fromDateTimes(checkIn, checkOut).length("days");

    var htls = _.cloneDeep(hotels);

    htls.forEach((h) => {
      h.rooms = (h?.rooms || []).filter((r) => {
        var valid = true;
        if (filters.board && filters.board !== r.board_code) {
          valid = false;
        }

        if (filters.room_type && filters.room_type !== r.type) {
          valid = false;
        }

        return valid;
      });
    });

    htls
      .filter((h) => (h?.rooms || []).length)
      .filter((h) => {
        const standard_count = (h?.rooms || []).filter((r) => !r.is_bar).length;
        if (standard_count === 0) {
          return false;
        } else {
          return true;
        }
      })
      .forEach((h) => {
        var valid_rooms = (h?.rooms || []).filter((r) => !r.is_bar);
        var bar_rooms = (h?.rooms || []).filter((r) => r.is_bar);

        if (!valid_rooms.length) {
          valid_rooms = h?.rooms || [];
        }

        h["computed_data"]["min_price"] = Math.min(
          ...valid_rooms.map((r) => r.price.value)
        );
        h["computed_data"]["max_price"] = Math.max(
          ...valid_rooms.map((r) => r.price.value)
        );
        h["computed_data"]["has_bar"] = bar_rooms.length > 0;
        h["computed_data"]["min_bar_price"] = Math.min(
          ...bar_rooms.map((r) => r.price.value)
        );
        if (h["computed_data"]["min_bar_price"] === Infinity) {
          h["computed_data"]["min_bar_price"] = 0;
        }

        if (h["computed_data"]["has_bar"]) {
          const min_price = h["computed_data"]["min_price"];
          const min_bar_price = h["computed_data"]["min_bar_price"];

          h["computed_data"]["bar_saving"] = min_bar_price - min_price;
          h["computed_data"]["bar_saving_perc"] = Number(
            (((min_bar_price - min_price) / min_price) * 100).toFixed()
          );

          if (h["computed_data"]["bar_saving"] < 0) {
            h["computed_data"]["bar_saving"] = 0;
            h["computed_data"]["bar_saving_perc"] = 0;
          }
        } else {
          h["computed_data"]["min_bar_price"] = 0;
          h["computed_data"]["bar_saving"] = 0;
          h["computed_data"]["bar_saving_perc"] = 0;
        }

        h["computed_data"]["stay"] = stay;
        h["computed_data"]["currency"] = h.rooms[0].price.currency;
      });

    var minPrice = 0;
    var maxPrice = 1000000000;
    if (filters.budget.length) {
      htls = htls.filter(
        (htl) =>
          htl.computed_data.min_price >= filters.budget[0] &&
          htl.computed_data.max_price <= filters.budget[1]
      );
    }

    if (filters.board) {
      htls = htls.filter((htl) =>
        htl.computed_data.available_board_codes.some((b) => filters.board === b)
      );
    }

    if (filters.rating.length) {
      htls = htls.filter((htl) =>
        filters.rating.includes(htl.metadata.rating + "")
      );
    }

    if (filters.room_type) {
      htls = htls.filter((htl) =>
        htl.computed_data.available_room_types.some((r) => {
          const code = r.includes(".") ? r.split(".")[0] : r;
          return filters.room_type === code;
        })
      );
    }

    if (filters.provider) {
      htls = htls.filter((htl) =>
        (htl.computed_data?.available_providers || []).includes(
          filters.provider
        )
      );
    }

    const coords = filters?.nearby?.nearby_meta?.computed_data?.geodata ?? {};
    if (filters.nearby.nearby_meta && !_.isEmpty(coords)) {
      htls = htls
        .map((htl) => {
          var center = turf.point([coords.lat, coords.lng]);
          var hotel = turf.point([
            htl.metadata.geodata.lat,
            htl.metadata.geodata.lng,
          ]);
          var options = { units: "kilometers" };

          var distance = turf.distance(center, hotel, options);
          htl["computed_data"]["distanceFromSelection"] = distance;
          return htl;
        })
        .sort(
          (a, b) =>
            a.computed_data.distanceFromSelection -
            b.computed_data.distanceFromSelection
        );
    } else {
      htls = htls
        .filter((htl) => {
          // TODO: Maybe we should use a .some filter here.
          return (
            minPrice <= htl.computed_data.min_price &&
            htl.computed_data.min_price <= maxPrice
          );
        })
        .sort((a, b) => {
          if (filters.order_by === "PRD") {
            return a.computed_data.min_price - b.computed_data.min_price;
          } else if (filters.order_by === "PRU") {
            return b.computed_data.min_price - a.computed_data.min_price;
          } else if (filters.order_by === "RAD") {
            return a.metadata.rating - b.metadata.rating;
          } else if (filters.order_by === "RAU") {
            return b.metadata.rating - a.metadata.rating;
          } else {
            return 0;
          }
        });

      if (filters.order_by === "STA") {
        htls = _.orderBy(
          htls,
          ["metadata.rating", "computed_data.min_price"],
          ["desc", "asc"]
        );
      } else if (filters.order_by === "BIG") {
        htls = _.orderBy(htls, ["computed_data.bar_saving"], ["desc"]);
      }

      if (filters.name) {
        const options = {
          shouldSort: true,
          keys: ["metadata.name"],
          threshold: 0.35,
        };
        const fuse = new Fuse(htls, options);
        htls = fuse.search(filters.name).map((res) => res.item);
      }
    }

    return htls;
  }
);

const getViewingHotel = (state) => state.HotelSearch.viewingHotel;
export function getAllowNonRefundable(state) {
  return state?.WebsiteMetadata?.metadata?.non_refundable_hotels ?? false;
}

export function getRoomComputedData(room) {
  return {
    has_cxl_data: room.cancellation_policies.length > 0,
    refundable: room.cancellation_policies.some((f) => f.type === "refundable"),
  };
}

export function getHotelDetails({
  details,
  allow_non_refundable,
  market_type = "B2C",
}) {
  if (_.isEmpty(details)) return null;

  const { metadata, operational_data, session_id } = details;
  var detailed_rooms = details?.detailed_rooms ?? [];
  // If detailed rooms are not returned then we use the original room list.
  // Usually when this happens is due to a glitch of the supplier API.
  if (!detailed_rooms.length) {
    detailed_rooms = details?.rooms ?? [];
  }

  detailed_rooms.forEach((room) => {
    room["computed_data"] = getRoomComputedData(room);
  });

  detailed_rooms = detailed_rooms
    .filter((r) => !r.is_bar) // Filter out rooms that are not bookable
    .filter((room) => {
      if (!allow_non_refundable) return room.computed_data.refundable;
      return true;
    });

  var grouped_rooms = {};
  // If this is a single rooom search we group by name so as to not display
  // a huge list of rooms. Out of each group we only select the cheapest
  // representative.
  // If this is a multiple room search then we just group by room id and
  // display the available rooms as is.
  if (operational_data.rooms.length === 1) {
    grouped_rooms = _.groupBy(detailed_rooms, "name");
  } else {
    grouped_rooms = _.groupBy(detailed_rooms, "id");
  }

  grouped_rooms = Object.entries(grouped_rooms)
    .map(([___, items]) => {
      // No more grouping is required if the search is for multiple rooms.
      if (operational_data.rooms.length > 1) {
        return items;
      }

      // Group same named rooms by board and cancellation policy.
      const list = _.groupBy(items, (i) => {
        const cData = i.computed_data;
        return `${i.board_code}___${cData.refundable}___${cData.has_cxl_data}`;
      });

      var rooms = [];
      if (market_type === "B2B") {
        rooms = Object.entries(list)
          .map(([___, items]) => items)
          .flat();
      } else {
        rooms = Object.entries(list).map(([___, items]) => {
          const sorted = items.sort((a, b) => a.price.value - b.price.value);
          return sorted[0];
        });
      }

      return rooms;
    })
    .sort((a, b) => {
      const aMin = Math.min(...a.map((r) => r.price.value));
      const bMin = Math.min(...b.map((r) => r.price.value));
      return aMin - bMin;
    });

  return {
    metadata,
    operational_data,
    detailed_rooms,
    grouped_rooms,
    session_id,
  };
}

export const getHotelDetailsSelector = createSelector(
  [getViewingHotel, getAllowNonRefundable],
  (details, allow_non_refundable) =>
    getHotelDetails({ details, allow_non_refundable })
);

export const getHotelFiltersSelector = createSelector(
  [getHotelFilters],
  (filters) => filters
);

function getBoardAvailableOptions(state) {
  const hotels = state?.HotelSearch?.hotels ?? [];
  const availableOptions = _.uniqWith(
    hotels.map((htl) =>
      htl.rooms.map((room) => [room.board_code, room.board_display])
    ),
    _.isEqual
  );

  return [...new Map(_.flatten(availableOptions))];
}

export const boardAvailableOptionsSelector = createSelector(
  [getBoardAvailableOptions],
  (boardAvailableOptions) => boardAvailableOptions
);

export const getHotelFiltersMetaSelector = createSelector(
  [getHotels, getWebsiteCurrencySelector],
  (hotels, currency) => {
    const room_types = [
      ...new Set(
        _.flatten(
          hotels.map((ht) =>
            ht.computed_data.available_room_types.map((t) =>
              t.includes(".") ? t.split(".")[0] : t
            )
          )
        )
      ),
    ]
      .filter((r) => r)
      .map((r) => {
        return {
          label: _.startCase(r),
          value: r,
        };
      });

    var minPrice = Math.min(
      ...hotels.map((htl) =>
        Math.min(...htl.rooms.map((room) => room.price.value))
      )
    );

    var maxPrice = Math.max(
      ...hotels.map((htl) =>
        Math.max(...htl.rooms.map((room) => room.price.value))
      )
    );

    const budget = { minPrice, maxPrice };

    function getRating(htl) {
      return htl.metadata.rating;
    }
    const htls = _.groupBy(hotels, getRating);
    const rating_count = {};
    Object.entries(htls).map(
      (entry) => (rating_count[entry[0]] = entry[1].length)
    );

    const available_providers = Object.entries(
      _.countBy(
        hotels
          .map((htl) => htl?.computed_data?.available_providers || [])
          .flat()
      )
    ).map(([key, count]) => [key, `${_.capitalize(key)} (${count})`]);
    return { room_types, budget, currency, rating_count, available_providers };
  }
);

export const isFetchingHotelDetailsSelector = createSelector(
  [getViewingHotel],
  (details) => {
    return details?.loading ?? false;
  }
);

const getPrebookHotel = (state) =>
  state.HotelSearch?.selectedHotel?.prebookData;

export const getPrebookHotelPriceSelector = createSelector(
  [getPrebookHotel],
  (details) => {
    if (!details) {
      return { value: 0, currency: "" };
    }

    var rooms = details?.detailed_rooms ?? [];
    if (!rooms.length) {
      rooms = details?.rooms ?? [];
    }

    const roomGroups = _.groupBy(rooms, "id");
    const room = Object.values(roomGroups)[0][0];
    return { value: room.price.value, currency: room.price.currency };
  }
);

export const getPrebookMetaSelector = createSelector(
  [getPrebookHotel],
  (details) => {
    if (!details) {
      return null;
    }

    return {
      metadata: details.metadata,
      operational_data: details.operational_data,
    };
  }
);
